/**
 * Maps the Details step's form values to the API request shape.
 * Handles field renames, type coercions, mode-specific branches
 * (manual vs self-schedule), and strips UI-only fields.
 */
import type { ScheduleMode } from '../config/types';
import { isSelfScheduleMode } from '../config/modeClassifiers';
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';
import {
  scheduleTypeFor,
  type FindTimeValue,
} from '../calendar/selfSchedule/findTime/modes';
import type { ContextIds, BookedSlot, ScheduleSlot } from './types';

// UI-only field names the DetailsStep carries in `payload.details`
// that the wizard doesn't need to send to the API. `bookedSlot` and
// `selfScheduleSlots` are deliberately NOT in this list — they drive
// mapDetailsToApi's start_time/end_time/time_slots emission and must
// survive into that mapper. The UI-only keys below would otherwise
// be silently dropped by the backend permit list, but we strip them
// defensively so request bodies stay small + logs readable.
const UI_ONLY_FIELDS = [
  'dateTime',
  'findTime',
  'book_meeting_room',
  'candidatePhone',
  'candidate_phone',
  'isCandidatePhoneEditing',
  'isDateTimeEditing',
] as const;

export function stripUiOnlyFields(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const cleaned = { ...obj };
  for (const key of UI_ONLY_FIELDS) {
    delete cleaned[key];
  }
  return cleaned;
}

export function applyContextIdsForMode(
  ids: ContextIds,
  mode: ScheduleMode
): Record<string, unknown> {
  const isSelfSchedule = isSelfScheduleMode(mode);
  // /ats_schedules accepts `contact_id` but NOT `candidate_id`; the
  // interview endpoint is the opposite. Rename here so the caller can
  // keep a single `candidate_id` field in the wizard's context IDs.
  if (isSelfSchedule && typeof ids.candidate_id === 'number') {
    const { candidate_id, ...rest } = ids;
    return { ...rest, contact_id: candidate_id };
  }
  return { ...ids };
}

export function mapDetailsToApi(
  details: Record<string, unknown>,
  mode: ScheduleMode
): Record<string, unknown> {
  const isSelfSchedule = isSelfScheduleMode(mode);

  const mapped: Record<string, unknown> = {};

  // Interview name: form uses `interviewName`, API uses `name`
  if ('interviewName' in details) {
    mapped.name = details.interviewName;
  }

  // Interview type field name diverges between endpoints:
  //   /ats_interviews — accepts `kind` (and also `interview_kind` for new
  //   callers); legacy call sites use `kind`.
  //   /ats_schedules — accepts ONLY `interview_kind` (see
  //   ats_schedules_helper.rb schedule_params_keys).
  // Send the right field per mode so self-schedule doesn't silently drop
  // the value.
  if ('interview_type' in details) {
    if (isSelfSchedule) {
      mapped.interview_kind = details.interview_type;
    } else {
      mapped.kind = details.interview_type;
    }
  }

  // Participant IDs: always send as number array
  if ('participant_ids' in details) {
    const raw = details.participant_ids;
    if (Array.isArray(raw)) {
      mapped.participant_ids = raw.map(Number);
    } else if (raw) {
      mapped.participant_ids = [Number(raw)];
    } else {
      mapped.participant_ids = [];
    }
  }

  // Duration — NOT in /ats_interviews permit list. Backend computes
  // it from (end_time - start_time) on the attendee record. For
  // /ats_schedules duration IS permitted, so forward it there.
  if (isSelfSchedule && 'duration' in details) {
    mapped.duration = Number(details.duration) || 30;
  }

  if ('location' in details) {
    mapped.location = details.location;
  }

  if (details.host_id) {
    mapped.host_id = Number(details.host_id);
  }

  if (details.ats_office_id) {
    mapped.ats_office_id = Number(details.ats_office_id);
  }

  // HackerRank template — only when location is hackerrank.
  if (details.location === 'hackerrank' && details.ext_interview_template_id) {
    mapped.ext_interview_template_id = details.ext_interview_template_id;
  }

  // Calendar visibility: form stores boolean, API expects string
  if ('calendar_visibility' in details) {
    mapped.calendar_visibility = details.calendar_visibility
      ? 'private'
      : 'default';
  }

  if ('enable_note_taker' in details) {
    mapped.note_taker_enabled = Boolean(details.enable_note_taker);
  }

  // Calendar info — only when plan allows shared calendars
  const sharedCalEnabled = Boolean(details.shared_calendar_enabled);
  const rawCal = details.calendar_info as
    | { provider_calendar_id?: string }
    | null
    | undefined;
  const hasCalendarInfo =
    sharedCalEnabled &&
    rawCal &&
    typeof rawCal === 'object' &&
    typeof rawCal.provider_calendar_id === 'string' &&
    rawCal.provider_calendar_id.length > 0;

  if (hasCalendarInfo) {
    mapped.calendar_info = details.calendar_info;
  }

  // Meeting rooms: map email id(s) to meeting_rooms_attributes format.
  // Single-select ships a bare string, multi-select ships string[] —
  // normalise both to string[] before emitting so the backend sees the
  // room when the user picked exactly one.
  if ('meeting_room_ids' in details) {
    const raw = details.meeting_room_ids;
    const emails: string[] = Array.isArray(raw)
      ? raw.filter((v): v is string => typeof v === 'string' && v.length > 0)
      : typeof raw === 'string' && raw.length > 0
        ? [raw]
        : [];
    if (emails.length > 0) {
      mapped.meeting_rooms_attributes = emails.map((email) => ({
        email,
        name: email,
      }));
    }
  }

  // Timezone — normalise to canonical IANA name before sending.
  if ('timezone' in details) {
    const raw = typeof details.timezone === 'string' ? details.timezone : '';
    mapped.timezone = raw ? normalizeTimeZoneForAtsCalendarApi(raw) : raw;
  }

  // Manual scheduling: extract start_time/end_time from bookedSlot.
  if (!isSelfSchedule) {
    const bookedSlot = details.bookedSlot as BookedSlot | null | undefined;
    if (bookedSlot?.start && bookedSlot?.end) {
      mapped.start_time = bookedSlot.start;
      mapped.end_time = bookedSlot.end;
    }
  }

  // Self-schedule: extract time_slots + schedule_type.
  if (isSelfSchedule) {
    const slots = details.selfScheduleSlots as ScheduleSlot[] | undefined;
    if (Array.isArray(slots) && slots.length > 0) {
      mapped.time_slots = slots.map((slot) => ({
        start_at: slot.start,
        end_at: slot.end,
        is_custom: slot.is_custom ?? false,
        ...(slot.override_type ? { override_type: slot.override_type } : {}),
      }));
    }
    const findTimeValue = (details.findTimeValue ?? null) as FindTimeValue;
    mapped.schedule_type = scheduleTypeFor(findTimeValue);
    mapped.schedule_slots_modified = true;
    mapped.schedule_type_modified = true;

    if (typeof details.bufferBeforeMinutes === 'number') {
      mapped.buffer_before = details.bufferBeforeMinutes;
    }
    if (typeof details.bufferAfterMinutes === 'number') {
      mapped.buffer_after = details.bufferAfterMinutes;
    }
  }

  return mapped;
}
